import { motion } from "framer-motion";
import {
  BookOpen, Scale, Landmark, Compass, Globe, Award, Medal, ArrowUpRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO, { orgSchema, breadcrumbSchema } from "@/components/SEO";
import { SectionHeading, FinalCTA } from "@/components/sections";
import { Reveal, stagger, item } from "@/components/motion";
import { useAppointment } from "@/components/AppointmentContext";
import philosopherPortrait from "@/assets/philosopher.png";
import { FOUNDER } from "@/data/founder";

const branchIcons = { Ethics: Compass, Logic: Scale, Metaphysics: Landmark };

export default function Therapists() {
  const { openDialog } = useAppointment();
  const crumbs = [{ name: "Home", path: "/" }, { name: "Meet the Founder", path: "/therapists" }];

  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: `${FOUNDER.name}, ${FOUNDER.credentials}`,
    jobTitle: FOUNDER.role,
    worksFor: { "@type": "MedicalBusiness", name: "Beacon Counseling, LLC" },
    alumniOf: "Central Connecticut State University",
    knowsAbout: ["Cognitive Behavioral Therapy", "Stoicism", "PTSD", "Trauma", "Anxiety", "Depression"],
    image: FOUNDER.photo,
  };

  return (
    <>
      <SEO title="Meet the Founder" path="/therapists"
        description="Meet Dragan David Pecirep, MS, LPC — founder of Beacon Counseling in Southington, CT. A Licensed Professional Counselor offering evidence-based CBT to adolescents and adults, informed by Stoic philosophy. U.S. Army combat veteran and PTSD/TBI researcher."
        image={FOUNDER.photo}
        jsonLd={[orgSchema, breadcrumbSchema(crumbs), personSchema]} />

      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden bg-navy pt-32 pb-20 lg:pt-40 lg:pb-24">
        <div className="pointer-events-none absolute -top-32 -right-20 h-96 w-96 rounded-full bg-teal/20 blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 -left-20 h-80 w-80 rounded-full bg-softblue-500/20 blur-3xl" />
        <div className="pointer-events-none absolute inset-0 grain opacity-[0.1]" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-12">
          <Reveal>
            <nav className="mb-8 flex items-center gap-2 text-sm text-white/50" aria-label="Breadcrumb">
              <a href="/" className="hover:text-teal-light transition-colors">Home</a>
              <span className="text-white/25">/</span><span className="text-white/80">Meet the Founder</span>
            </nav>
          </Reveal>
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <div>
              <Reveal>
                <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-sm text-teal-light">Meet the Founder</span>
              </Reveal>
              <Reveal delay={0.05}>
                <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.03] tracking-tight text-white text-balance">
                  {FOUNDER.name}
                </h1>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-2 text-lg font-medium text-teal-light">{FOUNDER.credentials} · {FOUNDER.role}</p>
              </Reveal>
              <Reveal delay={0.15}>
                <p className="mt-6 max-w-xl text-lg text-white/65 leading-relaxed">{FOUNDER.intro}</p>
              </Reveal>
              <Reveal delay={0.2}>
                <div className="mt-7 flex flex-wrap gap-2">
                  {FOUNDER.specialties.map((s) => (
                    <span key={s} className="rounded-full bg-white/10 px-4 py-1.5 text-sm text-white/80">{s}</span>
                  ))}
                </div>
              </Reveal>
              <Reveal delay={0.25}>
                <Button onClick={openDialog} data-testid="founder-book-btn"
                  className="mt-8 rounded-full bg-teal hover:bg-teal-dark text-white h-13 px-7 hover:-translate-y-0.5 transition-transform">
                  Book with David <ArrowUpRight className="ml-1 h-4 w-4" />
                </Button>
              </Reveal>
            </div>

            <Reveal delay={0.15}>
              <div className="relative mx-auto max-w-md">
                <div className="pointer-events-none absolute -inset-4 rounded-[2.5rem] bg-gradient-to-br from-teal/30 to-softblue-500/20 blur-2xl" />
                <img src={FOUNDER.photo} alt={`${FOUNDER.name}, ${FOUNDER.credentials}`}
                  className="relative w-full rounded-[2rem] object-cover shadow-premium aspect-[4/5]" data-testid="founder-photo" />
                <div className="absolute -bottom-5 left-5 right-5 rounded-2xl glass px-5 py-3 flex items-center gap-4">
                  <img src={FOUNDER.signature} alt="Signature" className="h-10 w-auto object-contain" />
                  <span className="text-xs text-navy/60 leading-tight">Founder,<br/>Beacon Counseling, LLC</span>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===== CLINICAL BACKGROUND ===== */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <div className="grid gap-12 lg:grid-cols-3 lg:gap-16">
            <div className="lg:col-span-2">
              <SectionHeading eyebrow="Clinical Background" title="A counselor grounded in philosophy and practice." />
              <div className="mt-10 space-y-7">
                {FOUNDER.clinicalBackground.map((p, i) => (
                  <Reveal key={i} delay={i * 0.05}>
                    <p className="text-lg sm:text-xl text-navy/75 leading-[1.85] max-w-2xl">{p}</p>
                  </Reveal>
                ))}
              </div>
            </div>
            <Reveal delay={0.1}>
              <div className="rounded-3xl border border-navy-100 bg-secondary p-8 lg:sticky lg:top-28">
                <h3 className="font-display text-2xl text-navy">At a glance</h3>
                <div className="mt-7 space-y-7">
                  {FOUNDER.profile.map((group) => (
                    <div key={group.label} className="border-t border-navy-100 pt-5 first:border-t-0 first:pt-0">
                      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-teal">{group.label}</div>
                      <ul className="mt-3 space-y-2.5">
                        {group.items.map((it) => (
                          <li key={it} className="flex gap-2.5 text-[0.975rem] leading-relaxed text-navy/85">
                            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-teal" />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===== STOICISM & CBT ===== */}
      <section className="bg-secondary py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <SectionHeading center eyebrow="Stoicism & Cognitive Behavioral Therapy"
            title="Where ancient wisdom meets modern science." />
          <div className="mx-auto mt-10 max-w-3xl space-y-7 text-center">
            {FOUNDER.stoicismIntro.map((p, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <p className="text-lg sm:text-xl text-navy/75 leading-[1.85]">{p}</p>
              </Reveal>
            ))}
          </div>

          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="mt-16 grid gap-6 md:grid-cols-3">
            {FOUNDER.branches.map((b) => {
              const Ic = branchIcons[b.name] || Compass;
              return (
                <motion.div key={b.name} variants={item}
                  className="rounded-3xl border border-navy-100 bg-white p-8 shadow-soft">
                  <span className="grid h-13 w-13 place-items-center rounded-2xl bg-softblue/60 p-3 text-teal"><Ic className="h-6 w-6" /></span>
                  <h3 className="mt-5 font-display text-2xl text-navy">{b.name}</h3>
                  <p className="text-sm font-medium text-teal">{b.tag}</p>
                  <p className="mt-3 text-[0.975rem] text-navy/70 leading-relaxed">{b.text}</p>
                </motion.div>
              );
            })}
          </motion.div>

          <div className="mx-auto mt-16 max-w-3xl space-y-7">
            {FOUNDER.stoicismOutro.map((p, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <p className="text-lg sm:text-xl text-navy/75 leading-[1.85]">{p}</p>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <figure className="mx-auto mt-16 max-w-2xl rounded-3xl bg-navy p-10 text-center sm:p-12">
              <img src={philosopherPortrait} alt="Marcus Aurelius, Stoic philosopher"
                className="mx-auto h-28 w-28 rounded-full object-cover ring-2 ring-white/15 shadow-premium" />
              <blockquote className="mt-7 font-display text-3xl italic text-white leading-snug sm:text-[2.15rem]">
                "It is not events themselves that disturb us, but the judgments we make about them."
              </blockquote>
              <figcaption className="mt-6 text-sm uppercase tracking-[0.2em] text-teal-light/80">— Epictetus</figcaption>
            </figure>
          </Reveal>
        </div>
      </section>

      {/* ===== COMMUNITY & GLOBAL ===== */}
      <section className="py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <SectionHeading eyebrow="Community Involvement" title="Rooted in Southington." />
              <div className="mt-10 space-y-7">
                {FOUNDER.community.map((p, i) => (
                  <Reveal key={i} delay={i * 0.05}>
                    <p className="text-lg sm:text-xl text-navy/75 leading-[1.85]">{p}</p>
                  </Reveal>
                ))}
              </div>
            </div>
            <Reveal delay={0.1}>
              <div className="grid gap-4 sm:grid-cols-2 self-start">
                {[
                  { icon: Landmark, t: "Southington Pediatric Associates", d: "Mental health affiliate" },
                  { icon: BookOpen, t: "ECCS & Resource Directory", d: "Listed community provider" },
                  { icon: Award, t: "ECCS & Bread for Life Grants", d: "Psychoeducational CBT programs" },
                  { icon: Globe, t: "International Service", d: "Studied in Europe · taught in Nepal" },
                ].map((c) => (
                  <div key={c.t} className="rounded-2xl border border-navy-100 bg-white p-5 shadow-soft">
                    <span className="grid h-11 w-11 place-items-center rounded-xl bg-softblue/60 text-teal"><c.icon className="h-5 w-5" /></span>
                    <h4 className="mt-4 font-medium text-navy leading-tight">{c.t}</h4>
                    <p className="mt-1 text-sm text-muted-foreground">{c.d}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===== MILITARY & LEADERSHIP ===== */}
      <section className="relative overflow-hidden bg-navy py-24 lg:py-28">
        <div className="pointer-events-none absolute inset-0 grain opacity-[0.1]" />
        <div className="pointer-events-none absolute -top-20 right-1/4 h-80 w-80 rounded-full bg-teal/15 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-12">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-start">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-sm text-teal-light">
                <Medal className="h-4 w-4" /> Military & Leadership Background
              </span>
              <h2 className="mt-5 font-display text-4xl sm:text-5xl tracking-tight text-white text-balance">
                A veteran's understanding of trauma and resilience.
              </h2>
              <div className="mt-10 space-y-7">
                {FOUNDER.military.map((p, i) => (
                  <Reveal key={i} delay={i * 0.05}>
                    <p className="text-lg sm:text-xl text-white/70 leading-[1.85]">{p}</p>
                  </Reveal>
                ))}
              </div>
            </div>
            <Reveal delay={0.1}>
              <div className="rounded-3xl glass-navy p-8 lg:p-9 lg:mt-24">
                <h3 className="font-display text-2xl text-white">Research & Recovery</h3>
                <p className="mt-5 text-lg text-white/75 leading-[1.85]">
                  Discharged honorably at the rank of Sergeant, David later spent three years researching
                  post-traumatic stress disorder (PTSD) and traumatic brain injury (TBI) with the U.S.
                  Department of Veterans Affairs and Yale University.
                </p>
                <p className="mt-4 text-lg text-white/75 leading-[1.85]">
                  That work — alongside his own recovery — deeply informs the compassion and clarity he
                  brings to trauma-focused clinical care today.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <FinalCTA title="Work with David." text="If David's approach resonates with you, take the first step. Reach out to begin evidence-based CBT tailored to your goals and values." />
    </>
  );
}
